import React from 'react'
import styled from 'styled-components'
import { useLocation } from 'react-router-dom'
import OMSLogo from 'src/assets/images/OMSLogoWht.png'

const resultFromSearch = search => {
  const params = new URLSearchParams(search)
  const status = (
    params.get('status') ||
    params.get('checkout') ||
    ''
  ).toLowerCase()
  if (status === 'success' || status === 'cancel') return status
  return 'success'
}

const venueIdFromSearch = search => {
  const venueId = new URLSearchParams(search).get('venueId')
  return venueId && String(venueId).trim() ? String(venueId).trim() : null
}

export const subscriptionReturnPath = venueId =>
  venueId ? `/v/${venueId}/subscriptions` : '/venues'

/**
 * Public post-Stripe Checkout page for app + dashboard.
 * No login required — offers a clear route back to the venue subscription page.
 */
const SubscriptionCheckoutResult = () => {
  const { search } = useLocation()
  const status = resultFromSearch(search)
  const venueId = venueIdFromSearch(search)
  const isSuccess = status === 'success'
  const returnHref = subscriptionReturnPath(venueId)

  return (
    <Page>
      <Header>
        <Logo src={OMSLogo} alt='Orders Made Simple' />
      </Header>
      <Card>
        <StatusDot $success={isSuccess} />
        <Title>
          {isSuccess ? 'Subscription successful' : 'Checkout cancelled'}
        </Title>
        <Body>
          {isSuccess
            ? 'Your payment went through. Return to OMS to see your plan on the subscriptions page.'
            : 'No payment was taken. You can return to OMS and try again anytime from the subscriptions page.'}
        </Body>
        <ReturnButton href={returnHref}>Return to OMS</ReturnButton>
        <Hint>You can also safely close this browser tab.</Hint>
      </Card>
    </Page>
  )
}

const Page = styled.div`
  min-height: 100vh;
  background: linear-gradient(165deg, #1a2e1a 0%, #2d4a2d 45%, #1f3320 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1.25rem 3rem;
  font-family: 'Source Sans 3', 'Segoe UI', sans-serif;
`

const Header = styled.header`
  width: 100%;
  max-width: 28rem;
  margin-bottom: 2.5rem;
  display: flex;
  justify-content: center;
`

const Logo = styled.img`
  height: 2.25rem;
  width: auto;
`

const Card = styled.main`
  width: 100%;
  max-width: 28rem;
  background: #f7faf5;
  border-radius: 4px;
  padding: 2.25rem 1.75rem;
  text-align: center;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
`

const StatusDot = styled.div`
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  margin: 0 auto 1.25rem;
  background: ${props => (props.$success ? '#5a8f3a' : '#a67c52')};
`

const Title = styled.h1`
  margin: 0 0 1rem;
  font-size: 1.65rem;
  font-weight: 700;
  color: #1a2e1a;
  letter-spacing: -0.02em;
`

const Body = styled.p`
  margin: 0 0 1.5rem;
  font-size: 1.05rem;
  line-height: 1.55;
  color: #334433;
`

const ReturnButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 12rem;
  padding: 0.85rem 1.5rem;
  margin-bottom: 1.25rem;
  background: #2d5a27;
  color: #f7faf5;
  font-size: 1.05rem;
  font-weight: 650;
  text-decoration: none;
  border-radius: 4px;
  transition: background 0.15s ease;

  &:hover,
  &:focus {
    background: #23471f;
    color: #f7faf5;
  }
`

const Hint = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: #6b7a6b;
`

export default SubscriptionCheckoutResult
